/**
 * Wishlist store and controls.
 *
 * Shopify has no wishlist, so this is local to the browser: a list of product
 * HANDLES in localStorage. That choice has a real trade-off and it is worth
 * being honest about it - the list does not follow the visitor to another
 * device, and it is lost if they clear their browser data. The alternative,
 * storing it against a customer account, needs accounts plus an App Proxy to
 * write metafields, which is a different project. Local works for everyone
 * today, including the majority who never sign in.
 *
 * HANDLES rather than IDs, and rather than whole product objects. A handle is
 * enough to fetch a fresh card from the Section Rendering API, so a saved
 * product's price, stock and image are always current - a stored copy would
 * go stale the moment the merchant edits anything, and a wishlist quietly
 * showing last month's price is worse than no wishlist.
 *
 * Everything reacts to one event. Buttons on cards, the header count and the
 * wishlist page all listen for `wishlist:changed` rather than knowing about
 * each other, so a product saved from a card updates the header without the
 * two ever being wired together.
 */
(function () {
  var KEY = 'waimers-wishlist';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      /* Corrupt JSON or a locked-down browser. An empty list is the safe
         reading: the visitor sees an empty wishlist rather than an error. */
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* Private mode, or the quota is full. The in-memory result of this
         action still stands for the current page, so the visitor is not shown
         a failure they can do nothing about. */
    }
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { list: list } }));
  }

  var Wishlist = {
    all: read,

    has: function (handle) {
      return read().indexOf(handle) !== -1;
    },

    count: function () {
      return read().length;
    },

    toggle: function (handle) {
      if (!handle) return false;
      var list = read();
      var at = list.indexOf(handle);
      if (at === -1) {
        /* Newest first. A wishlist is a queue of things being considered, and
           the most recent thought is the one the visitor is looking for. */
        list.unshift(handle);
      } else {
        list.splice(at, 1);
      }
      write(list);
      return at === -1;
    },

    remove: function (handle) {
      var list = read();
      var at = list.indexOf(handle);
      if (at === -1) return;
      list.splice(at, 1);
      write(list);
    },
  };

  window.Wishlist = Wishlist;

  /* ------------------------------------------------------------------ */
  /* Buttons                                                             */
  /* ------------------------------------------------------------------ */
  function syncButton(button) {
    var saved = Wishlist.has(button.getAttribute('data-wishlist-handle'));
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.classList.toggle('is-saved', saved);
  }

  function syncAll() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      var n = Wishlist.count();
      el.textContent = String(n);
      /* Hidden at zero rather than showing a 0 badge - an empty count is
         noise, and its absence is the clearer signal. */
      el.hidden = n === 0;
    });
  }

  /* Delegated, so buttons inside markup fetched later - quick view, the
     wishlist page itself, filtered collection results - work without being
     wired up again. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    /* A card's heart sits inside the card's link. Without this, saving a
       product also navigates to it. */
    event.preventDefault();
    event.stopPropagation();
    var saved = Wishlist.toggle(button.getAttribute('data-wishlist-handle'));
    /* A ring bursts out of the heart when something is saved - on the
       click only, never on page load for hearts that were already full.
       Off and on again across a forced reflow so a quick re-save replays. */
    if (saved) {
      button.classList.remove('is-bursting');
      void button.offsetWidth;
      button.classList.add('is-bursting');
    }
  });

  document.addEventListener('wishlist:changed', syncAll);

  /* Another tab is the same visitor. Without this, saving on one tab leaves
     the other showing a stale heart and a stale count. */
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) syncAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  document.addEventListener('shopify:section:load', syncAll);
})();
